import { useEffect, useRef, useState } from 'react';

import { Flex, Text, Link, Box, useTheme } from '@chakra-ui/react';

import { useNavigate, useLocation } from 'react-router-dom';

import { MdExpandLess, MdExpandMore } from 'react-icons/md';

/**
 * Width of the collapsed rail. Shared with SideBar so the hover flyout starts
 * flush against it rather than floating away from the icons.
 */
export const COLLAPSED_RAIL_WIDTH = '68px';

/**
 * Grace period before a hover flyout closes, so the cursor can cross the strip
 * between the rail item and the panel without the panel disappearing. Long
 * enough for an imprecise diagonal, short enough that a deliberate exit still
 * feels immediate.
 */
const FLYOUT_CLOSE_DELAY_MS = 300;

export type NavItemProps = {
  icon: (props: any) => JSX.Element;
  title: string;
  toggle: boolean;
  onToggle: (selectedPath: string) => void;
  subItems?: any[];
  path?: string;
  /** Collapsed rail: show the icon only, with the label in a hover flyout. */
  isCollapsed?: boolean;
  /** Whether this item is the one currently showing its hover flyout. */
  isFlyoutOpen?: boolean;
  /** Claim the flyout for this item, closing any other that is open. */
  onFlyoutOpen?: () => void;
  /** Release the flyout, unless another item has already claimed it. */
  onFlyoutClose?: () => void;
};

export default function NavItem({
  icon,
  title,
  path,
  subItems,
  toggle,
  onToggle,
  isCollapsed = false,
  isFlyoutOpen = false,
  onFlyoutOpen,
  onFlyoutClose,
}: NavItemProps) {
  const theme = useTheme();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Self-managed rather than a Chakra Menu/Popover: those open on click and
  // lay an overlay over the rest of the sidebar, which swallows clicks on the
  // items behind it.
  //
  // Which item is open lives in SideBar so that entering one icon closes the
  // previous panel immediately; only the offset is local, since it is this
  // item's own geometry.
  const [flyoutTop, setFlyoutTop] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const showFlyout = isCollapsed && isFlyoutOpen && flyoutTop !== null;

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  // The sidebar clips its overflow, so the flyout is positioned against the
  // viewport instead and takes its offset from the item that opened it.
  const openFlyout = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isCollapsed) {
      return;
    }

    cancelClose();
    setFlyoutTop(event.currentTarget.getBoundingClientRect().top);
    onFlyoutOpen?.();
  };

  /**
   * The panel is painted beside the rail but is a child of this item, which the
   * sidebar pads in from the rail edge. That left a strip between the two that
   * belonged to neither, so moving towards the panel fired mouseleave and shut
   * it before the cursor arrived.
   *
   * Closing on a short delay is the fly-out menu pattern the WAI tutorial
   * describes: it "makes it easier to use the menu when navigation by a mouse
   * is not very precise". Entering the panel cancels the timer, so a cursor in
   * transit keeps it open while one that leaves for good still dismisses it.
   * https://www.w3.org/WAI/tutorials/menus/flyout/
   *
   * The delay only covers leaving the rail. Moving to another icon swaps the
   * panel straight away, because that item claims the shared flyout state in
   * SideBar and this one stops rendering regardless of its pending timer.
   */
  const closeFlyoutSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setFlyoutTop(null);
      onFlyoutClose?.();
    }, FLYOUT_CLOSE_DELAY_MS);
  };

  useEffect(() => cancelClose, []);

  const active = path
    ? path === pathname
    : subItems?.some((s) => s.path?.includes(pathname));

  const activeColor = `${theme?.colors?.['primary']?.[500]}50`;

  return (
    <Flex
      my="8px"
      w="100%"
      flexDir="column"
      alignItems={'flex-start'}
      position="relative"
      onMouseEnter={openFlyout}
      onMouseLeave={closeFlyoutSoon}>
      <Link
        onClick={() => {
          // Collapsed, sub-items live in the flyout, so opening one here would
          // only expand a list the rail cannot show. Navigating items still
          // navigate; the rail stays as the user left it either way.
          if (isCollapsed) {
            if (path) {
              navigate(path);

              if (!active && path.includes('/transfers')) {
                window.location.reload();
              }
            }

            return;
          }

          onToggle(toggle ? '' : title);
          if (path) {
            navigate(path);

            if (!active && path.includes('/transfers')) {
              window.location.reload();
            }

            return;
          }
        }}
        backgroundColor={active || toggle ? activeColor : 'auto'}
        color={active || toggle ? 'support.100' : 'support.500'}
        p={2}
        borderRadius="10px"
        borderBottomRadius={toggle && Boolean(subItems) ? '0' : '10px'}
        _hover={{
          textDecor: 'none',
          backgroundColor: 'primary.500',
          color: 'support.100',
        }}
        justifyContent="center"
        w={'100%'}>
        <Flex align="center" textAlign="center">
          <Box w="100%">
            <Flex
              align="center"
              justify={isCollapsed ? 'center' : 'space-between'}>
              <Flex>
                {icon({
                  color: toggle
                    ? theme?.colors?.support[100]
                    : theme?.colors?.support[500],
                })}
                {!isCollapsed && (
                  <Text
                    ml={2}
                    display={'flex'}
                    userSelect="none"
                    fontWeight={active || toggle ? 'bold' : '400'}>
                    {title}
                  </Text>
                )}
              </Flex>
              {!isCollapsed &&
                Boolean(subItems) &&
                (!active && toggle ? <MdExpandLess /> : <MdExpandMore />)}
            </Flex>
          </Box>
        </Flex>
      </Link>

      {/* Hover flyout: in the rail this is the only place a label or a
          sub-item can appear, so it carries both.

          It starts flush against the rail and pads itself away from it rather
          than being offset by a margin, so there is no visible gap to cross.
          The cursor still leaves the nav item before it arrives here, which is
          what closeFlyoutSoon covers. */}
      {showFlyout && (
        <Box
          position="fixed"
          left={COLLAPSED_RAIL_WIDTH}
          top={`${flyoutTop}px`}
          pl="8px"
          zIndex={1400}
          onMouseEnter={cancelClose}
          onMouseLeave={closeFlyoutSoon}>
          <Box
            minW="200px"
            bg="#fff"
            borderRadius="10px"
            boxShadow="0 4px 20px rgba(0,0,0,0.15)"
            border="1px solid rgba(0,0,0,0.08)"
            py={2}>
            <Text
              px={3}
              py={1}
              fontWeight="bold"
              fontSize="14px"
              color="support.500"
              userSelect="none"
              cursor={path ? 'pointer' : 'default'}
              onClick={() => {
                if (!path) {
                  return;
                }

                navigate(path);

                if (!active && path.includes('/transfers')) {
                  window.location.reload();
                }
              }}>
              {title}
            </Text>
            {subItems?.map((subItem) => (
              <Text
                key={subItem?.path}
                px={3}
                py={2}
                fontSize="14px"
                color="support.500"
                cursor="pointer"
                userSelect="none"
                fontWeight={subItem.path.includes(pathname) ? 'bold' : '400'}
                _hover={{ bg: 'primary.800' }}
                onClick={() => navigate(subItem?.path)}>
                {subItem.title}
              </Text>
            ))}
          </Box>
        </Box>
      )}
      {toggle && !isCollapsed && (
        <Box
          bg={activeColor.slice(0, -2) + '30'}
          w="100%"
          borderBottomRadius="10px">
          {subItems?.map((subItem) => (
            <Flex key={subItem?.path} w="100%">
              <Link
                onClick={() => {
                  navigate(subItem?.path);
                }}
                p={2}
                borderRadius="10px"
                fontWeight={
                  subItem.path.includes('scans')
                    ? subItem.path === pathname
                      ? 'bold'
                      : '400'
                    : subItem.path.includes(pathname)
                    ? 'bold'
                    : '400'
                }
                justifyContent="center"
                w={'100%'}>
                <Flex w="100%">
                  <Box w="20px" />
                  <Text w="100%" color="support.500">
                    {subItem.title}
                  </Text>
                </Flex>
              </Link>
            </Flex>
          ))}
        </Box>
      )}
    </Flex>
  );
}
